import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { ActivatedRoute, Router } from '@angular/router';
import {
  IArchivosAdjuntos,
  IEmergencia,
  IFilter,
  IPopulate,
  IQueryParam,
} from 'modelos/src';
import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { HelperService } from '../../../../../auxiliares/servicios/helper.service';
import { Lista2Service } from '../../../../../auxiliares/servicios/lista2.service';
import {
  ArchivoSubido,
  SubirArchivoComponent,
} from '../../../../../auxiliares/subir-archivos-extendido/subir-archivos.component';
import { EmergenciasService } from '../../../../../auxiliares/servicios/http/emergencias.service';
import { LoginService } from '../../../../login/login.service';
import { AuxiliaresModule } from '../../../../../auxiliares/auxiliares.module';
import { SeguimientoEmergenciasComponent } from '../../../../standalone/eventos-emergencias/seguimientos/seguimiento.component';
import { DetallesEmergenciasInfoComponent } from '../detalles-emergencias-info/detalles-emergencias-info.component';
import { AccionesEmergenciaService } from '../acciones-emergencia/acciones-emergencia.service';
import {
  IPresentacion,
  estaFinalizada,
  presentacionEstado,
  presentacionPrioridad,
  presentacionSituacion,
  situacionDe,
} from '../../estado-emergencia';
import { CrearEditarEmergenciaComponent } from '../crear-editar-emergencia/crear-editar-emergencia.component';
import { FormularioDialogService } from '../../../../../auxiliares/servicios/formulario-dialog.service';
import { PIEZAS_DETALLE } from '../../../../../componentes/detalle/detalle.components';

/** Una acción de estado del menú del encabezado. */
interface AccionEstado {
  label: string;
  icono: string;
  color: string;
  visible: (e: IEmergencia) => boolean;
  ejecutar: (e: IEmergencia) => Promise<boolean>;
}

@Component({
  selector: 'app-detalles-emergencias',
  templateUrl: './detalles-emergencias.component.html',
  styleUrls: [
    '../../../../../componentes/detalle/detalle-pagina.scss',
    './detalles-emergencias.component.scss',
  ],
  standalone: true,
  imports: [
    AuxiliaresModule,
    SubirArchivoComponent,
    SeguimientoEmergenciasComponent,
    DetallesEmergenciasInfoComponent,
    ...PIEZAS_DETALLE,
  ],
})
export class DetallesEmergenciaComponent implements OnInit, OnDestroy {
  private activatedRoute = inject(ActivatedRoute);
  private router = inject(Router);
  private formularios = inject(FormularioDialogService);
  private lista2 = inject(Lista2Service);
  private emergenciasService = inject(EmergenciasService);
  private acciones = inject(AccionesEmergenciaService);
  helper = inject(HelperService);

  public loading = true;
  public selectedTab = 0;
  public idEmergencia = '';
  public emergencia?: IEmergencia;
  private emergencia$?: Subscription;
  /** Nombre de la emergencia en las migas: situación y código. */
  public tituloMiga?: string;
  private params$?: Subscription;
  private queryParams$?: Subscription;
  public estado: IPresentacion = presentacionEstado(null);
  public prioridad: IPresentacion = presentacionPrioridad(null);
  public situacion: IPresentacion = presentacionSituacion('Llamada');
  public accionesVisibles: AccionEstado[] = [];
  public ejecutandoAccion = false;

  // Documentación
  public archivosSubidos: ArchivoSubido[] = [];
  public imagenesCarrusel: string[] = [];
  private usuarioPropio = LoginService.getUsuario();
  /** Hay cambios en los archivos que todavía no se guardaron. */
  private archivosPendientes = false;
  public guardandoArchivos = false;
  private archivosChange$ = new Subject<void>();
  private archivosChangeSub?: Subscription;

  private readonly ACCIONES: AccionEstado[] = [
    {
      label: 'Asignar',
      icono: presentacionEstado('Asignada').icono,
      color: presentacionEstado('Asignada').color,
      visible: (e) => this.acciones.puedeAsignar(e),
      ejecutar: (e) => this.acciones.asignar(e),
    },
    {
      label: 'Reasignar',
      icono: presentacionEstado('Reasignada').icono,
      color: presentacionEstado('Reasignada').color,
      visible: (e) => this.acciones.puedeReasignar(e),
      ejecutar: (e) => this.acciones.reasignar(e),
    },
    {
      label: 'Llegó a destino',
      icono: presentacionEstado('Llegó a destino').icono,
      color: presentacionEstado('Llegó a destino').color,
      visible: (e) => this.acciones.puedeLlegadaDestino(e),
      ejecutar: (e) => this.acciones.llegadaDestino(e),
    },
    {
      label: 'Rumbo a hospital',
      icono: presentacionEstado('Rumbo a hospital').icono,
      color: presentacionEstado('Rumbo a hospital').color,
      visible: (e) => this.acciones.puedeRumboHospital(e),
      ejecutar: (e) => this.acciones.rumboHospital(e),
    },
    {
      label: 'Finalizar',
      icono: presentacionEstado('Finalizada').icono,
      color: presentacionEstado('Finalizada').color,
      visible: (e) => this.acciones.puedeFinalizar(e),
      ejecutar: (e) => this.acciones.finalizar(e),
    },
    {
      label: 'Cancelar',
      icono: presentacionEstado('Cancelada').icono,
      color: presentacionEstado('Cancelada').color,
      visible: (e) => this.acciones.puedeCancelar(e),
      ejecutar: (e) => this.acciones.cancelar(e),
    },
  ];

  get puedeEditar(): boolean {
    return (
      !!this.emergencia &&
      !estaFinalizada(this.emergencia) &&
      this.helper.tienePermiso('Emergencias Médicas - Editar')
    );
  }

  get puedeSubirArchivos(): boolean {
    return this.helper.tienePermiso('Emergencias Médicas - Editar');
  }

  // Solapas

  /** Las pestañas en orden, por nombre. Tiene que coincidir con la plantilla. */
  private pestanas(): string[] {
    return ['info', 'seguimiento', 'documentacion'];
  }

  public tabChanged(event: MatTabChangeEvent): void {
    this.selectedTab = event.index;
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { tab: this.pestanas()[event.index] ?? event.index },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  /** `tab` por nombre, o por índice (links viejos). */
  private indicePestana(tab: string): number | null {
    if (/^\d+$/.test(tab)) return parseInt(tab, 10);
    const i = this.pestanas().indexOf(tab);
    return i >= 0 ? i : null;
  }

  // Carga

  private listarEmergencia(): Promise<void> {
    const filter: IFilter<IEmergencia> = { _id: this.idEmergencia };
    const populate: IPopulate[] = [
      { path: 'destinatarioAsistencia' },
      { path: 'cliente', select: 'nombre' },
    ];
    const query: IQueryParam = {
      filter: JSON.stringify(filter),
      populate: JSON.stringify(populate),
      limit: 1,
      includeChildren: true,
    };
    return new Promise<void>((resolve) => {
      this.emergencia$?.unsubscribe();
      this.emergencia$ = this.lista2.emergencias(query, 15).subscribe({
        next: (data) => {
          this.emergencia = data?.datos?.[0];
          this.actualizarEncabezado();
          if (!this.archivosPendientes && !this.guardandoArchivos) {
            this.cargarArchivosAdjuntos();
          }
          resolve();
        },
        error: (error) => {
          this.helper.notifError(error);
          resolve();
        },
      });
    });
  }

  private actualizarEncabezado(): void {
    const e = this.emergencia;
    const sit = situacionDe(e);
    this.situacion = presentacionSituacion(sit);
    this.estado = presentacionEstado(e?.ultimoEventoEmergencia?.estado);
    this.prioridad = presentacionPrioridad(
      e?.prioridadCierre || e?.prioridadApertura,
    );
    this.accionesVisibles = e ? this.ACCIONES.filter((a) => a.visible(e)) : [];
    this.tituloMiga = e
      ? e.codigo
        ? `${this.situacion.label} #${e.codigo}`
        : this.situacion.label
      : undefined;
  }

  // Acciones

  public async ejecutar(accion: AccionEstado): Promise<void> {
    if (!this.emergencia || this.ejecutandoAccion) return;
    this.ejecutandoAccion = true;
    try {
      const hecho = await accion.ejecutar(this.emergencia);
      if (hecho) await this.listarEmergencia();
    } finally {
      this.ejecutandoAccion = false;
    }
  }

  /** Edita en diálogo; al guardar se recarga el detalle. */
  public editar(): void {
    const ref = this.formularios.abrir(CrearEditarEmergenciaComponent, {
      tamano: 'grande',
      data: { id: this.idEmergencia },
    });
    ref.afterClosed().subscribe((guardado) => {
      if (guardado) this.listarEmergencia();
    });
  }

  // Documentación

  private cargarArchivosAdjuntos(): void {
    this.archivosSubidos = (this.emergencia?.archivosAdjuntos || []).map(
      (adjunto: IArchivosAdjuntos) => ({
        url: adjunto.archivo || '',
        descripcion: adjunto.descripcion || '',
        fecha: adjunto.fechaSubida ? new Date(adjunto.fechaSubida) : new Date(),
        nombreOriginal: adjunto.nombreOriginal || 'archivo',
        size: 0,
      }),
    );
    this.imagenesCarrusel = this.obtenerImagenes();
  }

  private obtenerImagenes(): string[] {
    return this.archivosSubidos
      .filter((a) => this.esImagen(a.nombreOriginal))
      .map((a) => a.url);
  }

  private esImagen(nombreArchivo: string): boolean {
    const ext = nombreArchivo?.split('.').pop()?.toLowerCase() || '';
    return ['png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp', 'svg'].includes(ext);
  }

  public onArchivosChange(archivos: ArchivoSubido[]): void {
    this.archivosSubidos = archivos;
    this.imagenesCarrusel = this.obtenerImagenes();
    this.archivosPendientes = true;
    this.archivosChange$.next();
  }

  private async guardarArchivos(): Promise<void> {
    const archivosAdjuntos: IArchivosAdjuntos[] = this.archivosSubidos.map(
      (archivo) => ({
        fechaSubida: archivo.fecha.toISOString(),
        descripcion: archivo.descripcion,
        archivo: archivo.url,
        nombreOriginal: archivo.nombreOriginal,
        usuario: this.usuarioPropio?._id,
      }),
    );
    this.guardandoArchivos = true;
    this.archivosPendientes = false;
    try {
      await this.emergenciasService.update(this.idEmergencia, {
        archivosAdjuntos,
      });
      this.helper.notifSuccess('Documentación guardada');
    } catch (error) {
      this.archivosPendientes = true;
      this.helper.notifError(error);
    } finally {
      this.guardandoArchivos = false;
    }
  }

  // Hooks

  /**
   * Carga la emergencia del id de la ruta. Se llama cada vez que cambia el
   * id: Angular reutiliza el componente al ir de una emergencia a otra.
   */
  private async cargar(id: string | null): Promise<void> {
    // Lo que quedó sin guardar de la anterior se guarda antes de cambiar
    if (this.archivosPendientes) await this.guardarArchivos();
    this.loading = true;
    this.emergencia = undefined;
    this.idEmergencia = id || '';
    await this.listarEmergencia();
    this.loading = false;
  }

  ngOnInit(): void {
    this.archivosChangeSub = this.archivosChange$
      .pipe(debounceTime(1500))
      .subscribe(() => this.guardarArchivos());

    this.queryParams$ = this.activatedRoute.queryParams.subscribe((qp) => {
      const indice = qp['tab'] ? this.indicePestana(qp['tab']) : null;
      if (indice !== null) this.selectedTab = indice;
    });

    this.params$ = this.activatedRoute.paramMap.subscribe((params) => {
      void this.cargar(params.get('id'));
    });
  }

  ngOnDestroy(): void {
    this.emergencia$?.unsubscribe();
    this.params$?.unsubscribe();
    this.queryParams$?.unsubscribe();
    this.archivosChangeSub?.unsubscribe();
    // Si se sale antes del autoguardado, se guarda igual
    if (this.archivosPendientes) this.guardarArchivos();
  }
}
